import React, { useEffect } from 'react';
import { useForm } from '../../hooks/useForm';
import { FormInput, FormTextarea, FormSelect, Button } from '../ui';
import type { Ticket, TicketFormData } from '../../types/ticket';

interface TicketFormProps {
  ticket?: Ticket;
  onSubmit: (data: TicketFormData) => void;
  onCancel: () => void;
}

export const TicketForm: React.FC<TicketFormProps> = ({
  ticket,
  onSubmit,
  onCancel,
}) => {
  const { values, errors, handleChange, handleSubmit, setValues } = useForm<TicketFormData>({
    initialValues: {
      session_id: ticket?.session_id || '',
      title: ticket?.title || '',
      description: ticket?.description || '',
      status: ticket?.status || 'open',
      assistant_id: ticket?.assistant_id || null,
      user_account_id: ticket?.user_account_id || null,
      contact_id: ticket?.contact_id || null,
      whatsapp_id: ticket?.whatsapp_id || null,
      queue_id: ticket?.queue_id || null,
      from: ticket?.from || '',
      last_message: ticket?.last_message || '',
      integration_key: ticket?.integration_key || '',
    },
    onSubmit,
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (!values.title?.trim()) {
        errors.title = 'Título é obrigatório';
      }

      if (!ticket && !values.session_id?.trim()) {
        errors.session_id = 'ID da sessão é obrigatório';
      }

      return errors;
    },
  });

  useEffect(() => {
    if (ticket) {
      setValues({
        session_id: ticket.session_id,
        title: ticket.title,
        description: ticket.description || '',
        status: ticket.status,
        assistant_id: ticket.assistant_id,
        user_account_id: ticket.user_account_id,
        contact_id: ticket.contact_id,
        whatsapp_id: ticket.whatsapp_id,
        queue_id: ticket.queue_id,
        from: ticket.from || '',
        last_message: ticket.last_message || '',
        integration_key: ticket.integration_key || '',
      });
    }
  }, [ticket, setValues]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!ticket && (
          <FormInput
            label="ID da Sessão"
            name="session_id"
            value={values.session_id}
            onChange={handleChange}
            error={errors.session_id}
            required
          />
        )}

        <FormInput
          label="Título"
          name="title"
          value={values.title}
          onChange={handleChange}
          error={errors.title}
          required
        />

        <FormSelect
          label="Status"
          name="status"
          value={values.status}
          onChange={handleChange}
          error={errors.status}
        >
          <option value="open">Aberto</option>
          <option value="pending">Pendente</option>
          <option value="closed">Fechado</option>
        </FormSelect>

        <FormInput
          label="De"
          name="from"
          value={values.from || ''}
          onChange={handleChange}
          error={errors.from}
        />

        <FormInput
          label="Chave de Integração"
          name="integration_key"
          value={values.integration_key || ''}
          onChange={handleChange}
          error={errors.integration_key}
        />
      </div>

      <FormTextarea
        label="Descrição"
        name="description"
        value={values.description || ''}
        onChange={handleChange}
        error={errors.description}
        rows={4}
      />

      <FormTextarea
        label="Última Mensagem"
        name="last_message"
        value={values.last_message || ''}
        onChange={handleChange}
        error={errors.last_message}
        rows={3}
      />

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary">
          {ticket ? 'Atualizar' : 'Criar'} Ticket
        </Button>
      </div>
    </form>
  );
};
